// frontend/src/components/Header.tsx
import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  List,
  useMediaQuery,
  Menu,
  MenuItem,
  Typography,
  Badge,
  Avatar,
} from '@mui/material';
import {
  Home,
  People,
  Pets,
  CalendarMonth,
  PersonAdd,
  Event,
  Settings,
  Menu as MenuIcon,
  Logout,
  Error as ErrorIcon,
  History as HistoryIcon,
  BarChart as BarChartIcon,
  DarkMode,
  LightMode,
  MoreHoriz,
  NotificationsNone,
  Person,
  HelpOutline,
  InfoOutlined,
  NewReleases,
  HelpCenterOutlined,
  BugReport,
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { appAccessibilityConfig } from '../theme/themeConfig';
import { HEADER_BREAKPOINTS } from './forms/shared/MediaQueryUtils';
import useLocalisation from '../hooks/useLocalisation';
import { useAuth } from '../contexts/AuthContext';
import { useAppTheme } from '../contexts/ThemeContext';
import { useHeaderDrawer } from '../contexts/HeaderDrawerContext';
import { Permissions } from '../utils/permissions';
import { getCustomIcon } from '../utils/customIcons';
import { useNotifications } from '../hooks/useNotifications';
import {
  StyledDrawer,
  StyledListItem,
  StyledListItemText,
  StyledListItemIcon,
  DrawerSubMenu,
} from './menus/shared/DrawerMenuElements';

interface NavigationItem {
  path: string;
  labelKey: string;
  icon: React.ReactElement;
  // activePath is used when path contains a query string.
  activePath?: string;
}

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  minHeight: '64px',
  maxHeight: '64px',
  overflow: 'hidden',
  transition: appAccessibilityConfig.transitions.standard,
}));

const StyledToolbar = styled(Toolbar)({
  minHeight: '64px !important',
  paddingLeft: '16px',
  paddingRight: '16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  overflow: 'hidden',
});

const RightSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
});

const BaseIconButton = styled(IconButton)<{ component?: any; to?: string }>(
  ({ theme }) => ({
    color: 'white',
    width: '64px',
    height: '64px',
    borderRadius: '0',
    transition: appAccessibilityConfig.transitions.standard,
    transform: 'none',
    '&:hover': { transform: 'none !important' },
    '&:focus': {
      outline: appAccessibilityConfig.focusOutline,
      outlineOffset: appAccessibilityConfig.focusOffset,
    },
  })
);

const HamburgerButton = styled(BaseIconButton)(({ theme }) => ({
  border: 'none',
  backgroundColor: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.secondary.main,
    transform: 'none !important',
  },
  '& svg': { transition: 'transform .3s ease-in-out' },
  '&:hover svg': { transform: 'scale(1.8)' },
}));

const HomeButton = styled(BaseIconButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.secondary.main,
    transform: 'none !important',
  },
  '& svg': { transition: 'transform 0.3s ease-in-out' },
  '&:hover svg': { transform: 'scale(1.8)' },
}));

const ActionButton = styled(BaseIconButton)(({ theme }) => ({
  border: 'none',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    transform: 'none !important',
  },
}));

// Profile avatar button used on both mobile and desktop right rail
const ProfileButton = styled(IconButton)(({ theme }) => ({
  width: '64px',
  height: '64px',
  borderRadius: '0',
  color: 'white',
  backgroundColor: theme.palette.primary.main,
  transition: appAccessibilityConfig.transitions.standard,
  '&:hover': { backgroundColor: theme.palette.secondary.main },
  '&:focus': {
    outline: appAccessibilityConfig.focusOutline,
    outlineOffset: appAccessibilityConfig.focusOffset,
  },
}));

const LinkButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean; component?: any; to?: string }>(({ theme, active }) => ({
  fontSize: '1.4rem',
  fontWeight: 700,
  padding: '0 16px',
  borderRadius: '0',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  minWidth: '120px',
  height: '64px',
  whiteSpace: 'nowrap',
  color: 'white',
  backgroundColor: active ? theme.palette.secondary.main : 'transparent',
  border: 'none',
  transition: appAccessibilityConfig.transitions.standard,
  transform: 'none',
  '&:hover': {
    backgroundColor: theme.palette.secondary.main,
    transform: 'none !important',
  },
  '&:focus': {
    outline: appAccessibilityConfig.focusOutline,
    outlineOffset: appAccessibilityConfig.focusOffset,
  },
}));

const MoreButton = styled(IconButton)(({ theme }) => ({
  color: 'white',
  width: '120px',
  height: '64px',
  borderRadius: '0',
  backgroundColor: 'transparent',
  flexShrink: 0,
  gap: '4px',
  '&:hover': { backgroundColor: theme.palette.secondary.main },
  '&:focus': {
    outline: appAccessibilityConfig.focusOutline,
    outlineOffset: appAccessibilityConfig.focusOffset,
  },
}));

const OverflowMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: '#333',
    borderRadius: '0',
    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
    minWidth: '180px',
    marginTop: '0px',
  },
}));

const OverflowMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: '12px 20px',
  fontSize: '1.4rem',
  fontWeight: 700,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: 'white',
  '&:hover': { backgroundColor: theme.palette.secondary.main },
}));

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { nav, aria } = useLocalisation();
  const { user, logout } = useAuth();
  const { themeMode, toggleTheme } = useAppTheme();

  // Mobile drawer state shared via context so FloatingAccessButton can react.
  const {
    isHeaderDrawerOpen: mobileMenuOpen,
    setHeaderDrawerOpen: setMobileMenuOpen,
  } = useHeaderDrawer();

  const [isClient, setIsClient] = useState(false);
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const [overflowMenuAnchor, setOverflowMenuAnchor] =
    useState<null | HTMLElement>(null);

  const isMobile = useMediaQuery(
    `(max-width:${HEADER_BREAKPOINTS.mobile - 1}px)`,
    { noSsr: true }
  );

  // Each threshold = one 120px nav button added.
  // Fixed overhead: Home 64px + bell 64px + profile 64px = 192px.
  // Items (admin user, all permissions): Clients, Pets, Bookings, NewClient,
  // Quotes, NewQuote, Kennels, Calendar, Today = 9 × 120px = 1080px.
  // Cumulative thresholds from the left, each step adds one button:
  const showNewClient = useMediaQuery(
    `(min-width:${HEADER_BREAKPOINTS.showNewClient}px)`,
    { noSsr: true }
  );
  const showQuotes = useMediaQuery(
    `(min-width:${HEADER_BREAKPOINTS.showQuotes}px)`,
    { noSsr: true }
  );
  const showNewQuote = useMediaQuery(
    `(min-width:${HEADER_BREAKPOINTS.showNewQuote}px)`,
    { noSsr: true }
  );
  const showKennels = useMediaQuery(
    `(min-width:${HEADER_BREAKPOINTS.showKennels}px)`,
    { noSsr: true }
  );
  const showCalendar = useMediaQuery(
    `(min-width:${HEADER_BREAKPOINTS.showCalendar}px)`,
    { noSsr: true }
  );
  const showToday = useMediaQuery(
    `(min-width:${HEADER_BREAKPOINTS.showToday}px)`,
    { noSsr: true }
  );

  const { unreadCount } = useNotifications();
  const role = user?.role;
  const canViewClients = Permissions.canViewClients(role);
  const canManageSettings = Permissions.canManageSettings(role);
  const isAdmin = Permissions.isAdmin(role);
  const overflowMenuOpen = Boolean(overflowMenuAnchor);

  React.useEffect(() => {
    if (typeof window !== 'undefined' && document?.documentElement) {
      setIsClient(true);
    }
  }, []);

  // UTC today formatted YYYY-MM-DD for the Today nav link.
  const todayDateStr = new Date().toISOString().split('T')[0];

  const newClientItem: NavigationItem = {
    path: '/clients/new',
    labelKey: 'newClient',
    icon: <PersonAdd />,
  };
  const newQuoteItem: NavigationItem = {
    path: '/quote/new',
    labelKey: 'newQuote',
    icon: getCustomIcon('quotationadd', '2rem'),
  };
  const calendarItem: NavigationItem = {
    path: '/calendar',
    labelKey: 'calendar',
    icon: <CalendarMonth />,
  };
  const todayItem: NavigationItem = {
    path: `/today/check-ins?date=${todayDateStr}`,
    activePath: '/today/check-ins',
    labelKey: 'today',
    icon: <Event />,
  };

  const visibleNavItems: NavigationItem[] = [
    ...(canViewClients
      ? [
          { path: '/clients', labelKey: 'clients', icon: <People /> },
          { path: '/pets', labelKey: 'pets', icon: <Pets /> },
        ]
      : []),
    { path: '/bookings', labelKey: 'bookings', icon: <Event /> },
    ...(canViewClients && showNewClient ? [newClientItem] : []),
    ...(showQuotes
      ? [
          {
            path: '/quotes',
            labelKey: 'quotes',
            icon: getCustomIcon('quotation', '2rem'),
          },
        ]
      : []),
    ...(canViewClients && showNewQuote ? [newQuoteItem] : []),
    ...(showKennels
      ? [
          {
            path: '/kennels',
            labelKey: 'kennels',
            icon: getCustomIcon('kennel', '2rem'),
          },
        ]
      : []),
    ...(showCalendar ? [calendarItem] : []),
    ...(showToday ? [todayItem] : []),
  ];

  const overflowNavItems: NavigationItem[] = [
    ...(canViewClients && !showNewClient ? [newClientItem] : []),
    ...(!showQuotes
      ? [
          {
            path: '/quotes',
            labelKey: 'quotes',
            icon: getCustomIcon('quotation', '2rem'),
          },
        ]
      : []),
    ...(canViewClients && !showNewQuote ? [newQuoteItem] : []),
    ...(!showKennels
      ? [
          {
            path: '/kennels',
            labelKey: 'kennels',
            icon: getCustomIcon('kennel', '2rem'),
          },
        ]
      : []),
    ...(!showCalendar ? [calendarItem] : []),
    ...(!showToday ? [todayItem] : []),
  ];

  const adminNavItems: NavigationItem[] = canManageSettings
    ? [
        { path: '/settings', labelKey: 'settings', icon: <Settings /> },
        {
          path: '/admin/bookings-analytics',
          labelKey: 'bookingsAnalytics',
          icon: <BarChartIcon />,
        },
        {
          path: '/admin/activity-log',
          labelKey: 'activityLog',
          icon: <HistoryIcon />,
        },
        ...(isAdmin
          ? [
              {
                path: '/admin/error-logs',
                labelKey: 'errorLogs',
                icon: <ErrorIcon />,
              },
            ]
          : []),
        {
          path: '/admin/performance-dashboard',
          labelKey: 'performance',
          icon: getCustomIcon('performance', '1.6rem'),
        },
      ]
    : [];

  const mobileNavigationItems: NavigationItem[] = [
    { path: '/', labelKey: 'home', icon: <Home /> },
    ...(canViewClients
      ? [
          { path: '/clients', labelKey: 'clients', icon: <People /> },
          { path: '/pets', labelKey: 'pets', icon: <Pets /> },
        ]
      : []),
    { path: '/bookings', labelKey: 'bookings', icon: <Event /> },
    ...(canViewClients ? [newClientItem] : []),
    {
      path: '/quotes',
      labelKey: 'quotes',
      icon: getCustomIcon('quotation', '2rem'),
    },
    ...(canViewClients ? [newQuoteItem] : []),
    {
      path: '/kennels',
      labelKey: 'kennels',
      icon: getCustomIcon('kennel', '2rem'),
    },
    calendarItem,
    todayItem,
  ];

  const closeMenus = () => {
    if (isMobile && mobileMenuOpen) setMobileMenuOpen(false);
    setProfileDrawerOpen(false);
    setOverflowMenuAnchor(null);
  };

  const handleProfile = () => {
    navigate('/profile?tab=notifications');
    closeMenus();
  };
  const handleLogout = async () => {
    closeMenus();
    await logout();
    navigate('/login');
  };
  const handleMobileMenuToggle = () => setMobileMenuOpen(!mobileMenuOpen);
  const handleMobileMenuClose = () => setMobileMenuOpen(false);
  const handleMobileNavigation = (path: string) => {
    navigate(path);
    handleMobileMenuClose();
  };
  const handleProfileDrawerOpen = () => setProfileDrawerOpen(true);
  const handleProfileDrawerClose = () => setProfileDrawerOpen(false);
  const handleOverflowMenuOpen = (event: React.MouseEvent<HTMLElement>) =>
    setOverflowMenuAnchor(event.currentTarget);
  const handleOverflowMenuClose = () => setOverflowMenuAnchor(null);
  const handleThemeToggle = () => {
    toggleTheme();
    setProfileDrawerOpen(false);
  };

  const handleAbout = () => {
    navigate('/settings');
    closeMenus();
  };

  // What's New / Help Center / Report an issue are intentionally
  // placeholders for now (see About.tsx's own comment on this) - real
  // destinations are a future item once Tier 5 (Playwright) wraps.
  const helpItems = [
    { label: 'About', icon: <InfoOutlined />, onClick: handleAbout },
    {
      label: "What's New",
      icon: <NewReleases />,
      onClick: () => console.log("Help item clicked: What's New"),
    },
    {
      label: 'Help Center',
      icon: <HelpCenterOutlined />,
      onClick: () => console.log('Help item clicked: Help Center'),
    },
    {
      label: 'Report an issue',
      icon: <BugReport />,
      onClick: () => console.log('Help item clicked: Report an issue'),
    },
  ];

  const isDark = themeMode === 'dark';
  const username = user?.username || 'User';
  const initials = username.slice(0, 2).toUpperCase();
  const isNotificationsTab =
    new URLSearchParams(location.search).get('tab') === 'notifications';
  const isProfileTabActive =
    location.pathname === '/profile' && !isNotificationsTab;
  const isNotificationsTabActive =
    location.pathname === '/profile' && isNotificationsTab;

  if (!isClient) return null;

  return (
    <>
      <StyledAppBar position="sticky">
        <StyledToolbar>
          {!isMobile && (
            <HomeButton
              component={Link}
              to="/"
              aria-label={aria('homeButton')}
              title={aria('homeButton')}
            >
              <Home />
            </HomeButton>
          )}

          <Box sx={{ display: 'flex', gap: 0, flex: 1, overflow: 'hidden' }}>
            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 0 }}>
                {visibleNavItems.map((item) => (
                  <LinkButton
                    key={item.path}
                    component={Link}
                    to={item.path}
                    active={
                      location.pathname === (item.activePath ?? item.path)
                    }
                    aria-current={
                      location.pathname === (item.activePath ?? item.path)
                        ? 'page'
                        : undefined
                    }
                  >
                    {nav(item.labelKey as any)}
                  </LinkButton>
                ))}
              </Box>
            )}

            {isMobile && (
              <HamburgerButton
                onClick={handleMobileMenuToggle}
                aria-label={aria('openMenu')}
                aria-expanded={mobileMenuOpen}
                aria-haspopup="true"
              >
                <MenuIcon sx={{ fontSize: '2rem' }} />
              </HamburgerButton>
            )}
          </Box>

          {/* Overflow nav — desktop only, only when items don't fit */}
          {!isMobile && overflowNavItems.length > 0 && (
            <MoreButton
              onClick={handleOverflowMenuOpen}
              aria-label="More navigation items"
              aria-expanded={overflowMenuOpen}
              aria-haspopup="true"
            >
              <MoreHoriz sx={{ fontSize: '2rem' }} />
              <Typography
                sx={{
                  fontSize: '1.4rem',
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                }}
              >
                More
              </Typography>
            </MoreButton>
          )}

          {!isMobile && (
            <OverflowMenu
              anchorEl={overflowMenuAnchor}
              open={overflowMenuOpen}
              onClose={handleOverflowMenuClose}
              disableScrollLock
              MenuListProps={{ style: { width: '100%' } }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {overflowNavItems.map((item) => (
                <OverflowMenuItem
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    handleOverflowMenuClose();
                  }}
                >
                  {nav(item.labelKey as any)}
                </OverflowMenuItem>
              ))}
            </OverflowMenu>
          )}

          {/* Right rail — notification bell + profile avatar (both viewports) */}
          <RightSection>
            <ActionButton
              onClick={handleProfile}
              aria-label={aria('messagesButton')}
              title={aria('messagesButton')}
            >
              <Badge badgeContent={unreadCount} color="error" max={99}>
                <NotificationsNone sx={{ fontSize: '1.8rem' }} />
              </Badge>
            </ActionButton>

            <ProfileButton
              onClick={handleProfileDrawerOpen}
              aria-label="Open profile menu"
              aria-expanded={profileDrawerOpen}
              aria-haspopup="true"
            >
              <Avatar
                sx={{
                  width: 46,
                  height: 46,
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  bgcolor: 'transparent',
                  border: '2px solid rgba(255,255,255,0.7)',
                  color: 'white',
                }}
              >
                {initials}
              </Avatar>
            </ProfileButton>
          </RightSection>

          {/* Profile / system drawer — shared between mobile and desktop */}
          <StyledDrawer
            anchor="right"
            open={profileDrawerOpen}
            onClose={handleProfileDrawerClose}
          >
            <List disablePadding>
              <StyledListItem
                disableGutters
                active={isProfileTabActive}
                onClick={() => {
                  navigate('/profile');
                  handleProfileDrawerClose();
                }}
                sx={{ cursor: 'pointer' }}
              >
                <StyledListItemIcon active={isProfileTabActive}>
                  <Person />
                </StyledListItemIcon>
                <StyledListItemText
                  primary="My Profile"
                  active={isProfileTabActive}
                />
              </StyledListItem>

              <StyledListItem
                disableGutters
                active={isNotificationsTabActive}
                onClick={handleProfile}
                sx={{ cursor: 'pointer' }}
              >
                <StyledListItemIcon active={isNotificationsTabActive}>
                  <Badge badgeContent={unreadCount} color="error" max={99}>
                    <NotificationsNone />
                  </Badge>
                </StyledListItemIcon>
                <StyledListItemText
                  primary="Notifications"
                  active={isNotificationsTabActive}
                />
              </StyledListItem>

              {adminNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <StyledListItem
                    key={item.path}
                    disableGutters
                    active={isActive}
                    onClick={() => {
                      navigate(item.path);
                      handleProfileDrawerClose();
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    <StyledListItemIcon active={isActive}>
                      {item.icon}
                    </StyledListItemIcon>
                    <StyledListItemText
                      primary={nav(item.labelKey as any)}
                      active={isActive}
                    />
                  </StyledListItem>
                );
              })}

              <StyledListItem
                disableGutters
                onClick={handleThemeToggle}
                sx={{ cursor: 'pointer' }}
              >
                <StyledListItemIcon>
                  {isDark ? <LightMode /> : <DarkMode />}
                </StyledListItemIcon>
                <StyledListItemText
                  primary={isDark ? 'Light Mode' : 'Dark Mode'}
                />
              </StyledListItem>

              <DrawerSubMenu
                icon={<HelpOutline />}
                label="Help"
                items={helpItems}
              />

              <StyledListItem
                disableGutters
                onClick={handleLogout}
                sx={{
                  cursor: 'pointer',
                  color: 'secondary.main',
                  '&:hover': {
                    backgroundColor: 'secondary.main',
                    color: 'white',
                  },
                }}
              >
                <StyledListItemIcon sx={{ color: 'inherit' }}>
                  <Logout />
                </StyledListItemIcon>
                <StyledListItemText
                  primary={`Logout ${username}`}
                  sx={{ '& .MuiListItemText-primary': { color: 'inherit' } }}
                />
              </StyledListItem>
            </List>
          </StyledDrawer>
        </StyledToolbar>
      </StyledAppBar>

      {/* Mobile left drawer */}
      {isClient && typeof window !== 'undefined' && isMobile && (
        <StyledDrawer
          anchor="left"
          allowOverflowSwipe={false}
          open={mobileMenuOpen}
          onClose={handleMobileMenuClose}
        >
          <List disablePadding sx={{ flexGrow: 1 }}>
            {mobileNavigationItems.map((item) => {
              const activeMatch = item.activePath ?? item.path;
              const isActive = location.pathname === activeMatch;
              return (
                <StyledListItem
                  key={item.path}
                  disableGutters
                  active={isActive}
                  onClick={() => handleMobileNavigation(item.path)}
                  sx={{ cursor: 'pointer' }}
                >
                  <StyledListItemIcon active={isActive}>
                    {item.icon}
                  </StyledListItemIcon>
                  <StyledListItemText
                    primary={nav(item.labelKey as any)}
                    active={isActive}
                  />
                </StyledListItem>
              );
            })}
          </List>
        </StyledDrawer>
      )}
    </>
  );
};

export default Header;
